import {
  CombinedGraphQLErrors,
  MutationOptions,
  OperationVariables,
  QueryOptions,
  TypedDocumentNode,
} from '@apollo/client'
import { ResultOf } from '@graphql-typed-document-node/core'
import { getOperationName } from '@apollo/client/utilities/internal'

// TYPES

// Apollo 4 makes `variables` required on QueryOptions/MutateOptions when the
// operation has required variables. This codebase supplies variables separately
// at call time (see ApiSagas.clientCall), so the service definitions omit them.
export type QueryService<
  TVariables extends OperationVariables = OperationVariables,
  TData = any,
  Transformer = (response: ResultOf<TypedDocumentNode<TData, TVariables>>) => any,
> = Omit<QueryOptions<TVariables, TData>, 'variables'> & {
  variables?: TVariables
  name?: string
  transformer: Transformer
  persisted?: boolean
}

export type MutationService<
  TVariables extends OperationVariables = OperationVariables,
  TData = any,
  Transformer = (response: ResultOf<TypedDocumentNode<TData, TVariables>>) => any,
> = Omit<MutationOptions<TData, TVariables>, 'variables'> & {
  variables?: TVariables
  name?: string
  transformer: Transformer
}

export type ApiResponse<Service extends QueryService<any> | MutationService<any> = any> = {
  // TODO: conditionnal type to make transformer optionnal ?
  // data: Service extends { transformer: NonNullable<any> }
  //   ? ReturnType<Service['transformer']>
  //   : Service extends MutationService<TVariables, TData, Transformer>
  //   ? ResultOf<Service['mutation']>
  //   : Service extends QueryService<TVariables, TData, Transformer>
  //   ? ResultOf<Service['query']>
  //   : any
  data: ReturnType<Service['transformer']>
  errors: any
}

export type GraphqlError = {
  label?: string
  field?: string
}
export type GraphqlErrors = GraphqlError[]

// FUNCTIONS

export const getQueryService = <
  TVariables extends OperationVariables = OperationVariables,
  TData = any,
  Transformer = (response: ResultOf<TypedDocumentNode<TData, TVariables>>) => any,
>(
  service: QueryService<TVariables, TData, Transformer>
) => ({
  ...service,
  name: service.name || (getOperationName(service.query) ?? ''),
})

export const getMutationService = <
  TVariables extends OperationVariables = OperationVariables,
  TData = any,
  Transformer = (response: ResultOf<TypedDocumentNode<TData, TVariables>>) => any,
>(
  service: MutationService<TVariables, TData, Transformer>
) => ({
  ...service,
  name: service.name || (getOperationName(service.mutation) ?? ''),
})

const transformError = (error: any): GraphqlError => {
  if (error?.extensions?.category === 'validation') {
    const validation = error?.extensions[error?.extensions?.category]

    return Object.keys(validation).reduce(
      (r, v) => ({
        field: v.replace('input.', ''),
        label: validation[v][0],
      }),
      {}
    )
  }

  if (error?.message) {
    return {
      label: error.message,
    }
  }

  return {}
}

export const transformErrors = (errors: any): GraphqlErrors =>
  (errors && Array.isArray(errors) && errors.map(transformError)?.filter((e) => !!e.label)) || []

// Apollo 4 exposes a single `error` on query/mutate results (a
// CombinedGraphQLErrors for GraphQL errors, otherwise a network/other error)
// instead of v3's top-level `errors` array. Normalize it back to the array the
// sagas and transformErrors expect.
export const getResultErrors = (result: { error?: unknown; errors?: unknown }) => {
  if (result?.errors) {
    return result.errors
  }

  const error = result?.error
  if (!error) {
    return undefined
  }

  return CombinedGraphQLErrors.is(error) ? error.errors : [error]
}

export const enumOption = (
  t: any,
  name: string,
  value: string
): { label: string; value: string } => ({
  label: t(`enum_${name}_${value}`),
  value,
})
